/**
 * CctpDiffPanel — Comparaison visuelle entre l'ancien et le nouveau CCTP.
 *
 * Affiché dans MaterialThequePage post-extraction lorsque `?compare=1` est
 * présent dans l'URL : appelle `GET /api/materialtheque/projects/{pid}/cctp-diff`
 * et rend la liste des matériaux groupés par section, avec un statut
 * `added / removed / modified / unchanged`.
 *
 * Couleurs : vert (ajout), rouge (retrait), gold (modifié), gris (inchangé).
 */
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Plus, Minus, RefreshCw, Equal, ChevronDown, ChevronRight } from 'lucide-react';
import { HudCard, HudBadge } from '../hud';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

const STATUS_META = {
  added:     { color: 'rgba(34,197,94,1)',  bg: 'rgba(34,197,94,0.10)',  border: 'rgba(34,197,94,0.32)', icon: Plus,     label: 'Ajouté' },
  removed:   { color: 'rgba(239,68,68,1)',  bg: 'rgba(239,68,68,0.10)',  border: 'rgba(239,68,68,0.32)', icon: Minus,    label: 'Retiré' },
  modified:  { color: 'var(--c-gold)',       bg: 'var(--c-gold-soft)',    border: 'var(--c-border-accent)', icon: RefreshCw, label: 'Modifié' },
  unchanged: { color: 'var(--c-text-faint)', bg: 'var(--c-surface-2)',    border: 'var(--c-border)',       icon: Equal,    label: 'Inchangé' },
};

const DiffRow = ({ item }) => {
  const meta = STATUS_META[item.status] || STATUS_META.unchanged;
  const Icon = meta.icon;
  const showTypeChange = item.status === 'modified' && item.old?.material_type !== item.new?.material_type;
  return (
    <li
      data-testid={`diff-row-${item.status}-${(item.name || '').toLowerCase().replace(/\s+/g,'-')}`}
      className="rounded-lg px-3 py-2 flex items-center gap-3 transition-colors"
      style={{ background: meta.bg, border: `1px solid ${meta.border}` }}
    >
      <span
        className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
        style={{ background: 'rgba(0,0,0,0.20)', border: `1px solid ${meta.border}` }}
      >
        <Icon className="w-3 h-3" strokeWidth={2.5} style={{ color: meta.color }} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium truncate" style={{ color: 'var(--c-text)' }}>
          {item.name || '—'}
        </p>
        <p className="text-[11px]" style={{ color: 'var(--c-text-2)' }}>
          {showTypeChange ? (
            <>
              <span style={{ textDecoration: 'line-through', color: 'rgba(239,68,68,0.85)' }}>{item.old?.material_type || '—'}</span>
              {' → '}
              <span style={{ color: meta.color }}>{item.new?.material_type || '—'}</span>
            </>
          ) : (item.material_type || '—')}
        </p>
      </div>
      <span
        className="text-[10px] font-bold uppercase tracking-[0.14em] shrink-0"
        style={{ color: meta.color }}
      >
        {meta.label}
      </span>
    </li>
  );
};

const SectionGroup = ({ section, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const counts = useMemo(() => {
    const c = { added: 0, removed: 0, modified: 0, unchanged: 0 };
    (section.items || []).forEach((i) => { c[i.status] = (c[i.status] || 0) + 1; });
    return c;
  }, [section]);
  return (
    <div className="rounded-xl overflow-hidden" style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 transition-colors"
        data-testid={`diff-section-toggle-${section.section_id}`}
      >
        <div className="flex items-center gap-2.5">
          {open ? <ChevronDown className="w-4 h-4" style={{ color: 'var(--c-text-2)' }} /> : <ChevronRight className="w-4 h-4" style={{ color: 'var(--c-text-2)' }} />}
          <span className="text-[14px] font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)' }}>
            {section.label}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {counts.added > 0     && <HudBadge tone="success">+{counts.added}</HudBadge>}
          {counts.removed > 0   && <HudBadge tone="danger">-{counts.removed}</HudBadge>}
          {counts.modified > 0  && <HudBadge tone="gold">~{counts.modified}</HudBadge>}
          {counts.unchanged > 0 && (
            <span className="text-[11px]" style={{ color: 'var(--c-text-faint)' }}>={counts.unchanged}</span>
          )}
        </div>
      </button>
      {open && (
        <ul className="px-3 pb-3 pt-1 space-y-1.5" data-testid={`diff-section-items-${section.section_id}`}>
          {(section.items || []).map((it, idx) => (
            <DiffRow key={`${section.section_id}-${idx}`} item={it} />
          ))}
        </ul>
      )}
    </div>
  );
};

const CctpDiffPanel = ({ projectId, newJobId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hideUnchanged, setHideUnchanged] = useState(true);

  useEffect(() => {
    if (!projectId || !newJobId) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/materialtheque/projects/${projectId}/cctp-diff`, { ...opts, params: { new_job_id: newJobId } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, newJobId]);

  // useMemo must be called unconditionally (rules of hooks) — handle null data inside.
  const filteredSections = useMemo(() => {
    if (!data || !Array.isArray(data.sections)) return [];
    if (!hideUnchanged) return data.sections;
    return data.sections
      .map((s) => ({ ...s, items: (s.items || []).filter((i) => i.status !== 'unchanged') }))
      .filter((s) => s.items.length > 0);
  }, [data, hideUnchanged]);

  if (loading) {
    return (
      <HudCard variant="glass" data-testid="cctp-diff-loading">
        <p className="text-[13px]" style={{ color: 'var(--c-text-2)' }}>Calcul des différences en cours…</p>
      </HudCard>
    );
  }

  if (!data) return null;

  if (data.no_base) {
    return (
      <HudCard variant="glass" data-testid="cctp-diff-no-base">
        <span className="hud-eyebrow hud-eyebrow-gold">Comparaison CCTP</span>
        <p className="mt-1 text-[13px]" style={{ color: 'var(--c-text-2)' }}>
          Pas de version précédente approuvée pour ce projet — c'est ton premier CCTP, rien à comparer 🎉
        </p>
      </HudCard>
    );
  }

  const { added, removed, modified, unchanged } = data.summary || {};
  const totalChanges = (added || 0) + (removed || 0) + (modified || 0);

  return (
    <HudCard variant="glass" data-testid="cctp-diff-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <span className="hud-eyebrow hud-eyebrow-gold">Diff CCTP</span>
          <p
            className="mt-1 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}
          >
            {totalChanges === 0
              ? 'Aucun changement détecté'
              : `${totalChanges} changement${totalChanges > 1 ? 's' : ''} détecté${totalChanges > 1 ? 's' : ''}`}
          </p>
          <p className="text-[11.5px] mt-0.5" style={{ color: 'var(--c-text-2)' }}>
            Comparaison vs version approuvée précédente · Base : <code className="text-[10.5px]">{data.base_job_id?.slice(0,18)}…</code>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="hud-badge" style={{ background: 'rgba(34,197,94,0.10)', color: '#86EFAC', border: '1px solid rgba(34,197,94,0.32)' }}>+ {added || 0} ajouts</span>
          <span className="hud-badge" style={{ background: 'rgba(239,68,68,0.10)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.32)' }}>− {removed || 0} retraits</span>
          <span className="hud-badge" style={{ background: 'var(--c-gold-soft)', color: 'var(--c-gold)', border: '1px solid var(--c-border-accent)' }}>~ {modified || 0} modifs</span>
          <label className="flex items-center gap-1.5 text-[11px] cursor-pointer ml-2" style={{ color: 'var(--c-text-2)' }}>
            <input
              type="checkbox"
              checked={hideUnchanged}
              onChange={(e) => setHideUnchanged(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#D9A05B] cursor-pointer"
              data-testid="cctp-diff-hide-unchanged"
            />
            Masquer inchangés ({unchanged || 0})
          </label>
        </div>
      </div>

      <div className="space-y-2.5" data-testid="cctp-diff-sections">
        {filteredSections.map((s) => (
          <SectionGroup key={s.section_id} section={s} defaultOpen />
        ))}
        {filteredSections.length === 0 && (
          <p className="text-[12.5px] py-6 text-center" style={{ color: 'var(--c-text-faint)' }}>
            Aucune section avec des changements ne correspond au filtre.
          </p>
        )}
      </div>
    </HudCard>
  );
};

export default CctpDiffPanel;
